import React, { useCallback, useContext, useEffect, useState, useRef } from 'react';
import { Box, Button, Paper } from '@mui/material';
import { useOrganizationJiraTeamMembers } from '../hook/useOrganizationJiraTeamMembers';
import { useTeamMemberStates } from '../hook/useTeamMemberStates';
import { useOrgUsers } from '../hook/useOrgUsers';
import { useEntity } from '@backstage/plugin-catalog-react';
import { githubAuthApiRef, useApi } from '@backstage/core-plugin-api';
import { AlertSnackbar, AppContext, JiraItem } from '@softwareone/plugin-project-management-react';
import { Progress } from '@backstage/core-components';
import {
  addTeamMembers,
  extractGroupAdminInfo,
  processProjectTeamInfo,
  TeamResponse,
  UserObject,
} from '../utils/utils';
import { useUserProfile } from '@backstage/plugin-user-settings';
import { Actor } from '@softwareone/plugin-project-management-common/src/types/jira/actor/actor';
import { UserModel } from '@softwareone/plugin-project-management-common/src/types/jira/user/userModel';
import { ProjectUserData, SystemEntity } from '@softwareone/plugin-project-management-common';

export const JiraProject = () => {
  const { administrators, setAdministrators, contributors, setContributors, customers, setCustomers } =
    useTeamMemberStates();

  const [jiraProjectLeadAccountId, setJiraProjectLeadAccountId] = useState<string>('');
  const [jiraAdministratorsActors, setJiraAdministratorActors] = useState<Actor[]>([]);
  const [jiraContributorsActors, setJiraContributorsActors] = useState<Actor[]>([]);
  const [jiraCustomersActors, setJiraCustomersActors] = useState<Actor[]>([]);
  const [open, setOpen] = useState(false);
  const [jiraLoading, setJiraLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState<'success' | 'error'>('success');
  const [isFormValid, setIsFormValid] = useState(false);
  const hasFetchedTeamMembers = useRef(false);
  const hasFetchedProjectTeam = useRef(false);
  const { entity } = useEntity();

  let initialValueJiraVisibleForSwoEmployees: boolean | undefined;
  if (typeof entity.metadata?.jiraVisibilityForSwoEmployees === 'boolean') {
    initialValueJiraVisibleForSwoEmployees = entity.metadata.jiraVisibilityForSwoEmployees;
  } else if (typeof entity.metadata?.everyoneIsRepoReader === 'boolean') {
    initialValueJiraVisibleForSwoEmployees = entity.metadata.everyoneIsRepoReader;
  } else {
    initialValueJiraVisibleForSwoEmployees = undefined;
  }

  const {
    confluenceSpaceKey,
    jiraProjectKey,
    name: currentSystemName,
    customer,
    title,
    creatorActiveDirectoryId,
  } = entity.metadata as SystemEntity;
  const [jiraProjectKeyInput, setJiraProjectKeyInput] = useState<string | undefined>(
    (confluenceSpaceKey as string) || ''
  );
  const [jiraProjectTemplate, setJiraProjectTemplate] = useState<string | undefined>('');
  const [jiraVisibilityForSwoEmployees, setJiraVisibilityForSwoEmployees] = useState<boolean | undefined>(
    initialValueJiraVisibleForSwoEmployees
  );
  const [isOwner, setIsOwner] = useState<boolean | undefined>(false);
  const [prevUsers, setPrevUsers] = useState<{
    gitHubContributors: { login: string }[];
    gitHubReaders: { login: string }[];
    gitHubOwners: { login: string }[];
  }>();
  const [dispatchLoading, setDispatchLoading] = useState<boolean>(false);
  const { loading, error, value } = useOrganizationJiraTeamMembers();
  const {
    services: { jiraService, projectService },
  } = useContext(AppContext);
  const { prevUsersLoading, getEmailsUserNames } = useOrgUsers(projectService);
  const projectKey = currentSystemName;

  // Extract the second part of projectKey (after the dash) for Jira validation exception
  const projectKeyParts = projectKey.split('-');
  const projectKeyIdentifier = projectKeyParts.length > 1 ? projectKeyParts[1] : undefined;
  const gitHubApi = useApi(githubAuthApiRef);
  const { backstageIdentity, profile } = useUserProfile();
  const formattedProfile = { id: '', authProvider: '', ...profile };
  const userObject: UserObject = {
    type: backstageIdentity?.type || '',
    userEntityRef: backstageIdentity?.userEntityRef || '',
    ownershipEntityRefs: backstageIdentity?.ownershipEntityRefs || [],
  };

  const hasGithubAdmins: boolean = extractGroupAdminInfo(userObject);
  const getUsers = useCallback(async () => {
    if (hasFetchedTeamMembers.current) {
      return;
    }
    hasFetchedTeamMembers.current = true;
    setJiraLoading(true);
    try {
      const data = await jiraService.getTeamMembers(projectKey);
      const projectUserData = data as ProjectUserData;
      projectUserData?.jiraProjectLead && setJiraProjectLeadAccountId(projectUserData.jiraProjectLead.accountId);
      projectUserData?.projectRolesUserData?.jiraContributors &&
        setJiraContributorsActors(projectUserData.projectRolesUserData?.jiraContributors);
      projectUserData?.projectRolesUserData?.jiraAdministrators &&
        setJiraAdministratorActors(projectUserData.projectRolesUserData?.jiraAdministrators);
      projectUserData?.projectRolesUserData?.jiraCustomers &&
        setJiraCustomersActors(projectUserData.projectRolesUserData?.jiraCustomers);
      setJiraLoading(false);
    } catch (err) {
      setJiraLoading(false);
      console.error('Error:', err);
      setMessage('An error occurred trying to get the current Jira projects');
      setStatus('error');
      setOpen(true);
    }
  }, [jiraService, projectKey]);

  useEffect(() => {
    if (!jiraProjectKey) {
      return;
    }
    getUsers();
  }, [jiraProjectKey, getUsers]);

  const isUserOwner = useCallback(async () => {
    if (hasFetchedProjectTeam.current) {
      return;
    }
    hasFetchedProjectTeam.current = true;

    if (formattedProfile.authProvider === 'GitHub') {
      const profileData = await gitHubApi.getProfile();
      const teamData: TeamResponse = await processProjectTeamInfo(
        projectService,
        currentSystemName,
        hasGithubAdmins,
        setIsOwner,
        profileData
      );
      setPrevUsers({
        gitHubContributors: teamData.gitHubContributors,
        gitHubReaders: teamData.gitHubReaders,
        gitHubOwners: teamData.gitHubOwners,
      });
      return;
    }

    if (formattedProfile.id === creatorActiveDirectoryId) {
      setIsOwner(true);
      const data = await projectService.getProjectTeamInfo(projectKey);
      setPrevUsers(data);
    }
  }, [
    profile,
    hasGithubAdmins,
    formattedProfile.authProvider,
    formattedProfile.id,
    creatorActiveDirectoryId,
    gitHubApi,
    projectService,
    currentSystemName,
    projectKey,
  ]);

  useEffect(() => {
    isUserOwner();
  }, [isUserOwner]);

  const extractInitialAccountIds = (rawFormat: Actor[]) => {
    return rawFormat.map(entry => entry.actorUser.accountId);
  };

  useEffect(() => {
    setAdministrators(extractInitialAccountIds(jiraAdministratorsActors));
    setContributors(extractInitialAccountIds(jiraContributorsActors));
    setCustomers(extractInitialAccountIds(jiraCustomersActors));
  }, [
    jiraAdministratorsActors,
    jiraContributorsActors,
    jiraCustomersActors,
    setAdministrators,
    setContributors,
    setCustomers,
  ]);

  const getActors = (accountIds: string[], actors: Actor[]) => {
    const formattedActors: Actor[] = [];
    accountIds.forEach(accountId => {
      const foundActor = actors.find(formattedActor => formattedActor.actorUser.accountId === accountId);
      if (foundActor) {
        formattedActors.push(foundActor);
        return;
      }
      const foundValue = (value as UserModel[]).find(entry => entry.accountId === accountId);
      if (!foundValue) {
        return;
      }
      const actor: Actor = {
        id: 0,
        displayName: foundValue.publicName || '',
        type: 'atlassian-user-role-actor',
        actorUser: { accountId: foundValue.accountId },
      };
      formattedActors.push(actor);
    });
    return formattedActors;
  };

  type FormattedTeamMembers<T extends Actor | string> = {
    [key in
      | 'jiraCustomers'
      | 'jiraContributors'
      | 'jiraAdministrators'
      | 'jiraProjectLeadId']: key extends 'jiraProjectLeadId' ? string : T[];
  };

  const getFormattedTeamMembers = (withActorInfo?: boolean) => {
    const data = {
      jiraProjectLeadId: jiraProjectLeadAccountId,
      jiraContributors: getActors(contributors, jiraContributorsActors),
      jiraAdministrators: getActors(administrators, jiraAdministratorsActors),
      jiraCustomers: getActors(customers, jiraCustomersActors),
    };
    if (!withActorInfo) {
      return {
        ...data,
        jiraContributors: getActors(contributors, jiraContributorsActors).map(actor => actor.actorUser.accountId),
        jiraAdministrators: getActors(administrators, jiraAdministratorsActors).map(actor => actor.actorUser.accountId),
        jiraCustomers: getActors(customers, jiraCustomersActors).map(actor => actor.actorUser.accountId),
      };
    }
    return data;
  };

  const createJiraProject = async () => {
    setJiraLoading(true);
    const { jiraProjectLeadId, ...restUsers } = getFormattedTeamMembers() as FormattedTeamMembers<string>;
    const createProject = {
      projectKey,
      jiraProjectKey: jiraProjectKeyInput as string,
      templateName: jiraProjectTemplate as string,
      jiraVisibilityForSwoEmployees: !!jiraVisibilityForSwoEmployees,
      jiraProjectLead: jiraProjectLeadId,
      ...restUsers,
    };
    try {
      await jiraService.createJiraProject(createProject);
      setJiraLoading(false);
      setMessage(
        'Your Jira project has been successfully created. It takes time to be in sync with SoftwareOne DevOps platform'
      );
      setStatus('success');
      setOpen(true);
    } catch (err) {
      setJiraLoading(false);
      console.error('Error:', err);
      setMessage('An error occurred during the Jira project creation');
      setStatus('error');
      setOpen(true);
    }
  };
  const updateJiraTeamMembers = async () => {
    setJiraLoading(true);
    try {
      const updateProject = {
        projectKey,
        ...(getFormattedTeamMembers(true) as FormattedTeamMembers<Actor>),
        jiraVisibilityForSwoEmployees: jiraVisibilityForSwoEmployees,
      };
      await jiraService.updateTeamMembers(updateProject);
      setJiraLoading(false);
      setMessage('Your Jira project has been successfully updated');
      setStatus('success');
      setOpen(true);
    } catch (err) {
      setJiraLoading(false);
      console.error('Error:', err);
      setMessage('An error occurred during the Jira project update');
      setStatus('error');
      setOpen(true);
    }
  };

  const formattedValue: { accountId: string; publicName: string }[] = [];
  if (Array.isArray(value))
    (value as UserModel[]).forEach(entry => {
      if (entry?.publicName !== undefined && entry?.accountId !== undefined)
        formattedValue.push({ accountId: entry.accountId, publicName: entry.publicName });
    });

  const handleUpdateSubmit = async (e: React.SyntheticEvent) => {
    e.preventDefault();
    await updateJiraTeamMembers();
  };

  const handleCreateSubmit = async (e: React.SyntheticEvent) => {
    e.preventDefault();
    await createJiraProject();
  };

  useEffect(() => {
    if (loading) {
      return;
    }
    if (error) {
      console.log('Error:', error);
      setMessage('An error occurred trying to get all potential jira members');
      setStatus('error');
      setOpen(true);
    } else if (!Array.isArray(value)) {
      const ex = new Error(value.message);
      console.log('Error:', ex);
      setMessage('An error occurred trying to get all potential jira members');
      setStatus('error');
      setOpen(true);
    }
  }, [loading, error, value]);

  const handleClose = (_event?: React.SyntheticEvent, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpen(false);
  };

  type ChangeData = {
    jiraProjectTemplate?: string;
    jiraProjectKey?: string;
    jiraProjectLeadAccountId: string;
    jiraContributors: string[];
    jiraCustomers: string[];
    jiraAdministrators: string[];
    jiraVisibilityForSwoEmployees?: boolean;
  };
  const handleChange = (e: ChangeData) => {
    setJiraProjectTemplate(e.jiraProjectTemplate || '');
    setJiraProjectKeyInput(e.jiraProjectKey);
    setJiraProjectLeadAccountId(e.jiraProjectLeadAccountId);
    setContributors(e.jiraContributors);
    setAdministrators(e.jiraAdministrators);
    setCustomers(e.jiraCustomers);
    setJiraVisibilityForSwoEmployees(e.jiraVisibilityForSwoEmployees);
  };
  const generateJiraProjectName = () => {
    return `${customer}-${title}`;
  };
  const handleValidityChange = (e: boolean) => {
    setIsFormValid(e);
  };

  const getJiraUserNames = async () => {
    try {
      return await jiraService.getJiraOrgUsers();
    } catch (e) {
      console.error('Fetch error:', e);
    }
  };

  const performActions = async () => {
    setDispatchLoading(true);
    const userEmail = await getEmailsUserNames();
    const jiraEmail = await getJiraUserNames();
    if (!prevUsersLoading) {
      addTeamMembers(userEmail, jiraEmail, prevUsers, setAdministrators, setContributors, setCustomers, true);
    }
    setDispatchLoading(false);
  };

  if (loading || jiraLoading || dispatchLoading) {
    return <Progress />;
  }

  if (!jiraProjectKey) {
    return (
      <>
        <form onSubmit={handleCreateSubmit}>
          <Box>
            <Paper
              sx={{
                display: 'flex',
                flexDirection: 'column',
                m: 1,
              }}
            >
              <JiraItem
                jiraProjectName={generateJiraProjectName()}
                jiraProjectKey={jiraProjectKeyInput}
                jiraProjectTemplate={jiraProjectTemplate}
                jiraProjectLeadAccountId={jiraProjectLeadAccountId}
                jiraAdministrators={administrators}
                withSharedConfluenceSpace={!!confluenceSpaceKey}
                jiraContributors={contributors}
                jiraCustomers={customers}
                userList={formattedValue}
                onChange={handleChange}
                onValidityChange={handleValidityChange}
                jiraVisibilityForSwoEmployees={jiraVisibilityForSwoEmployees}
                isDisabled={!isOwner}
                getTeamMembersforJira={performActions}
                allowedProjectKeyException={projectKeyIdentifier}
                allowedConfluenceKeyException={confluenceSpaceKey}
              />
              <Button sx={{ m: '10px' }} variant="outlined" type="submit" disabled={!isFormValid || !isOwner}>
                Create
              </Button>
            </Paper>
          </Box>
        </form>
        <AlertSnackbar open={open} onClose={handleClose} severity={status} message={message} />
      </>
    );
  }

  return (
    <>
      <form onSubmit={handleUpdateSubmit}>
        <Paper
          sx={{
            display: 'flex',
            flexDirection: 'column',
            m: 1,
          }}
        >
          <JiraItem
            jiraProjectLeadAccountId={jiraProjectLeadAccountId}
            jiraAdministrators={administrators}
            jiraContributors={contributors}
            jiraCustomers={customers}
            userList={formattedValue}
            onValidityChange={handleValidityChange}
            jiraVisibilityForSwoEmployees={jiraVisibilityForSwoEmployees}
            onChange={handleChange}
            enableJiraProjectInfo={false}
            isDisabled={!isOwner}
            getTeamMembersforJira={performActions}
          />
          <Button sx={{ m: '10px' }} variant="outlined" disabled={!isFormValid || !isOwner} type="submit">
            Save
          </Button>
        </Paper>
      </form>
      <AlertSnackbar open={open} onClose={handleClose} severity={status} message={message} />
    </>
  );
};
